import { Badge } from '@awpmarine/ui/components/ui/badge';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowRightIcon, RssIcon } from 'lucide-react';

import { PageHero } from '@/components/marketing';
import { newsItems } from '@/components/news';
import { formatDate } from '@/lib/dates';
import { pageHead } from '@/lib/head';
import { ogImage } from '@/lib/open-graph';
import { m } from '@/paraglide/messages';

import { type NewsCategory, NewsList } from './-list';

export const metadata = () => ({ title: m.news_title(), description: m.news_description() });

export const Route = createFileRoute('/news/')({
  head: () =>
    pageHead({
      ...metadata(),
      canonical: '/news',
      image: ogImage('/news'),
      links: [{ rel: 'alternate', type: 'application/atom+xml', title: 'AWP Marine News', href: '/news/feed.atom' }],
    }),
  component: NewsPage,
});

function NewsPage() {
  const [lead, ...rest] = newsItems();

  const counts = new Map<string, number>();
  for (const item of rest) for (const tag of item.tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  const categories: NewsCategory[] = [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  return (
    <>
      <PageHero eyebrow={m.news_eyebrow()} title={m.news_heading()} lead={m.news_lead()}>
        <a
          href='/news/feed.atom'
          className='mt-7 inline-flex items-center gap-2 rounded-full border border-[#cfd7e2] px-4.5 py-2.5 font-semibold transition-colors hover:border-brand'
        >
          <RssIcon className='size-4.5 text-[#e8711b]' />
          {m.news_feed()}
        </a>
      </PageHero>

      {lead ? (
        <>
          <section className='mx-auto w-full max-w-310 px-5 pb-12 sm:px-8'>
            <Link
              to='/news/$'
              params={{ _splat: lead.slug }}
              className='bg-navy flex flex-wrap items-end gap-x-14 gap-y-6 rounded-[28px] p-8 text-white sm:p-12'
            >
              <span className='min-w-0 flex-[1.3_1_480px]'>
                <span className='text-on-navy mb-4.5 flex flex-wrap items-center gap-2.5 text-sm'>
                  {lead.tags[0] && (
                    <Badge className='bg-navy-soft h-auto rounded-full px-2.5 py-1 text-[13px] font-semibold text-[#d5e4f7]'>
                      {lead.tags[0]}
                    </Badge>
                  )}
                  {m.news_latest({ date: formatDate(lead.date) })}
                </span>
                <span className='block text-[clamp(28px,3vw,40px)] leading-[1.15] font-bold tracking-[-0.015em]'>
                  {lead.title}
                </span>
              </span>
              <span className='flex min-w-0 flex-[1_1_340px] flex-col gap-5'>
                {lead.description && (
                  <span className='text-on-navy text-[17px] leading-relaxed'>{lead.description}</span>
                )}
                <span className='inline-flex items-center gap-2 font-semibold'>
                  {m.news_read_more()} <ArrowRightIcon className='size-4.5' />
                </span>
              </span>
            </Link>
          </section>
          <section className='mx-auto w-full max-w-310 px-5 pb-16 sm:px-8 sm:pb-24'>
            <NewsList items={rest} categories={categories} />
          </section>
        </>
      ) : (
        <section className='mx-auto w-full max-w-310 px-5 pb-24 sm:px-8'>
          <p className='border-line text-body rounded-3xl border p-12 text-center'>{m.news_empty()}</p>
        </section>
      )}
    </>
  );
}
